import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRightIcon,
  CalendarDaysIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  PencilSquareIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { HandThumbDownIcon, HandThumbUpIcon } from "@heroicons/react/20/solid";
import OverflowMenu from "../../ui/OverflowMenu";
import { DifficultyBadge } from "../../Difficulty/Difficulty";
import { focusRing } from "../matrix/MatrixParts";
import { gradeLabel, toNumber } from "../matrix/matrixModel";
import { byQuestions, formatDue, personName, reviewLink, taskShare, taskTitle, writeLevels } from "./taskModel";

const linkClass = `inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg bg-blue-50 px-3 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-600/15 transition duration-200 hover:bg-blue-100 hover:text-blue-800 max-sm:min-h-11 ${focusRing}`;
const quietLinkClass = `inline-flex min-h-[2.25rem] items-center gap-1.5 rounded-lg px-3 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 max-sm:min-h-11 ${focusRing}`;
// The assignee's vote on a chosen question, as its chip shows it.
const VOTE_LOOKS = {
  UPVOTED: { Icon: HandThumbUpIcon, label: "folders.tasks.vote.up", cls: "bg-emerald-50 text-emerald-800 ring-emerald-600/20 hover:bg-emerald-100" },
  DOWNVOTED: { Icon: HandThumbDownIcon, label: "folders.tasks.vote.down", cls: "bg-red-50 text-red-700 ring-red-600/20 hover:bg-red-100" },
  none: { Icon: null, label: "folders.tasks.vote.none", cls: "bg-white text-slate-700 ring-slate-300 hover:bg-slate-50" },
};
// Chosen questions shown before "+N more".
const CHOSEN_SHOWN = 12;

function Bar({ share, done }) {
  return (
    <span className="block h-1.5 w-full overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
      <span className={`block h-full rounded-full ${done ? "bg-emerald-500" : "bg-blue-600"}`} style={{ width: `${Math.round(Math.min(1, share) * 100)}%` }} />
    </span>
  );
}

function StateBadge({ task }) {
  const [t] = useTranslation();
  const tone = task.cancelled
    ? ["bg-slate-100 text-slate-600 ring-slate-500/20", XCircleIcon, "folders.tasks.state.cancelled"]
    : task.done
      ? ["bg-emerald-50 text-emerald-800 ring-emerald-600/20", CheckCircleIcon, "folders.tasks.state.done"]
      : task.overdue
        ? ["bg-red-50 text-red-700 ring-red-600/20", ExclamationTriangleIcon, "folders.tasks.state.overdue"]
        : null;
  if (!tone) return null;
  const [cls, Icon, key] = tone;
  return (
    <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {t(key)}
    </span>
  );
}

// A write task: each planned level, approved of planned, and how many more were sent and wait.
function WriteProgress({ task }) {
  const [t] = useTranslation();
  const levels = writeLevels(task);
  const none = toNumber(task.progress?.submitted?.none);
  return (
    <div className="space-y-2">
      <ul className="grid gap-x-6 gap-y-2 sm:grid-cols-3">
        {levels.map(({ level, planned, approved, submitted }) => {
          const waiting = Math.max(0, submitted - approved);
          return (
            <li key={level} className="min-w-0">
              <div className="flex items-center justify-between gap-2 text-sm">
                <DifficultyBadge level={level} showPoints={false} />
                <span className="tabular-nums text-slate-700">
                  {t("folders.tasks.approved_of", { approved, planned })}
                </span>
              </div>
              <div className="mt-1.5">
                <Bar share={planned ? approved / planned : 0} done={approved >= planned} />
              </div>
              {waiting > 0 && <p className="mt-1 text-xs text-slate-500">{t("folders.tasks.waiting", { count: waiting })}</p>}
            </li>
          );
        })}
      </ul>
      {none > 0 && (
        <p className="flex items-center gap-1 text-xs text-amber-800">
          <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t("folders.tasks.no_difficulty", { count: none })}
        </p>
      )}
    </div>
  );
}

// A review task: how many of its questions the assignee has voted on, and how.
function ReviewProgress({ task }) {
  const [t] = useTranslation();
  const p = task.progress || {};
  const total = toNumber(p.total);
  const votes = [
    ["up", HandThumbUpIcon, "text-emerald-700", "question_detail.vote_bar.like"],
    ["down", HandThumbDownIcon, "text-red-700", "question_detail.vote_bar.dislike"],
  ].filter(([key]) => toNumber(p[key]) > 0);
  return (
    <div className="space-y-1.5">
      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm tabular-nums text-slate-700">
        <span>
          {total
            ? t("folders.tasks.voted_of", { voted: toNumber(p.voted), total })
            : t(byQuestions(task) ? "folders.tasks.no_questions_left" : "folders.tasks.no_questions_yet")}
        </span>
        {votes.map(([key, Icon, tone, label]) => (
          <span key={key} className={`inline-flex items-center gap-1 ${tone}`}>
            <Icon className="h-4 w-4" aria-hidden="true" />
            <span className="sr-only">{t(label)}:</span>
            {toNumber(p[key])}
          </span>
        ))}
      </p>
      {total > 0 && <Bar share={taskShare(task)} done={task.done} />}
    </div>
  );
}

// A review task's chosen questions: each a link to it, in the colour of the assignee's vote on it.
function ChosenQuestions({ task }) {
  const [t] = useTranslation();
  const [all, setAll] = useState(false);
  const list = task.questions || [];
  if (!list.length) return null;
  const shown = all ? list : list.slice(0, CHOSEN_SHOWN);
  return (
    <div className="mt-2">
      <p className="text-sm text-slate-500">{t("folders.tasks.questions_are")}</p>
      <ul className="mt-1.5 flex flex-wrap gap-1.5">
        {shown.map((question) => {
          const look = VOTE_LOOKS[question.vote] || VOTE_LOOKS.none;
          return (
            <li key={question.uuid}>
              <Link
                to={`/questions/${question.uuid}`}
                title={t(look.label)}
                className={`inline-flex min-h-[1.75rem] items-center gap-1 rounded-md px-2 text-xs font-medium tabular-nums ring-1 ring-inset transition duration-200 max-sm:min-h-9 ${look.cls} ${focusRing}`}
              >
                {look.Icon && <look.Icon className="h-3.5 w-3.5" aria-hidden="true" />}
                {question.code}
                <span className="sr-only">, {t(look.label)}</span>
              </Link>
            </li>
          );
        })}
        {list.length > CHOSEN_SHOWN && (
          <li>
            <button
              type="button"
              aria-expanded={all}
              onClick={() => setAll((value) => !value)}
              className={`inline-flex min-h-[1.75rem] items-center rounded-md px-2 text-xs font-medium text-blue-700 hover:bg-blue-50 max-sm:min-h-9 ${focusRing}`}
            >
              {all ? t("folders.tasks.questions_less") : t("folders.tasks.questions_more", { count: list.length - CHOSEN_SHOWN })}
            </button>
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * One task. `showAssignee`: whose it is (a subject's list for its moderator); `showFolder`: its exam and subject (My
 * tasks). `own`: the reader's own task, with its way to work on it (write a question in the subject; for a review,
 * first the questions it has not voted on yet). A review task opens its questions in the bank for its moderator too.
 * `onEdit` / `onCancel`: its moderator's actions.
 */
export default function TaskCard({ task, folderPath = "", showAssignee = false, showFolder = false, own = false, onEdit, onCancel }) {
  const [t, i18n] = useTranslation();
  const write = task.kind === "write";
  const authors = (task.authors || []).map(personName);
  const menu = !task.cancelled && (onEdit || onCancel)
    ? [
        onEdit && { key: "edit", label: t("folders.tasks.edit"), Icon: PencilSquareIcon, onClick: () => onEdit(task) },
        onCancel && { key: "cancel", label: t("folders.tasks.cancel"), Icon: XCircleIcon, danger: true, onClick: () => onCancel(task) },
      ]
    : [];
  const title = taskTitle(t, task);
  // The bank's filter chip names the task (and whose it is, for its moderator).
  const linkState = { taskLabel: showAssignee ? `${title} · ${personName(task.assignee)}` : title };
  const left = Math.max(0, toNumber(task.progress?.total) - toNumber(task.progress?.voted));
  const reviewLinks = write
    ? []
    : own && left
      ? [
          [reviewLink(task, { left: true }), t("folders.tasks.open_left", { count: left })],
          [reviewLink(task), t("folders.tasks.open_all")],
        ]
      : [[reviewLink(task), t("folders.tasks.open_questions")], left > 0 && [reviewLink(task, { left: true }), t("folders.tasks.open_unvoted", { count: left })]].filter(Boolean);
  return (
    <article className={`rounded-xl border bg-white p-4 shadow-sm sm:p-5 ${task.cancelled ? "border-slate-200 opacity-70" : task.overdue && !task.done ? "border-red-200" : "border-slate-200"}`}>
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex shrink-0 rounded-md px-1.5 py-0.5 text-xs font-medium ${write ? "bg-indigo-50 text-indigo-700" : "bg-violet-50 text-violet-700"}`}
            >
              {t(`folders.tasks.kind.${task.kind}`)}
            </span>
            <StateBadge task={task} />
          </div>
          <h3 className="mt-1.5 break-words text-base font-semibold text-slate-900">{title}</h3>
          <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-slate-500">
            {showFolder && folderPath && <span className="font-medium text-slate-700">{folderPath}</span>}
            {write && task.topic && <span>{gradeLabel(t, task.topic.grade)}</span>}
            {showAssignee && <span>{t("folders.tasks.assignee_is", { name: personName(task.assignee) })}</span>}
            {task.due_date ? (
              <span className={`inline-flex items-center gap-1 ${task.overdue && !task.done ? "font-medium text-red-700" : ""}`}>
                <CalendarDaysIcon className="h-4 w-4" aria-hidden="true" />
                {t("folders.tasks.due_on", { date: formatDue(task.due_date, i18n.language) })}
              </span>
            ) : (
              <span>{t("folders.tasks.no_due")}</span>
            )}
          </p>
        </div>
        {menu.length > 0 && <OverflowMenu label={t("folders.tasks.actions_of", { name: title })} items={menu} portal />}
      </div>

      {!write && authors.length > 0 && (
        <p className="mt-2 break-words text-sm text-slate-700">
          <span className="text-slate-500">{t("folders.tasks.authors_are")}</span> {authors.join(", ")}
        </p>
      )}
      {byQuestions(task) && <ChosenQuestions task={task} />}
      {write && task.in_matrix === false && !task.cancelled && (
        <p className="mt-2 flex items-start gap-1.5 text-sm text-amber-800">
          <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t("folders.tasks.not_in_matrix")}
        </p>
      )}

      <div className="mt-3">{write ? <WriteProgress task={task} /> : <ReviewProgress task={task} />}</div>
      {/* The assignee's way to do a review: a vote on each question. */}
      {!write && own && !task.done && !task.cancelled && (
        <p className="mt-2 flex items-start gap-1.5 text-sm text-slate-600">
          <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" aria-hidden="true" />
          {t("folders.tasks.how_to_review")}
        </p>
      )}

      {task.comment && (
        <p className="mt-3 flex items-start gap-2 whitespace-pre-line break-words rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
          <span className="min-w-0">{task.comment}</span>
        </p>
      )}

      {!task.cancelled && (write ? own : reviewLinks.length > 0) && (
        <div className="mt-3 flex flex-wrap gap-2">
          {write ? (
            <Link to={`/my-questions/create?folder=${task.folder?.uuid}&task=${task.uuid}`} state={{ from: "/my-tasks" }} className={linkClass}>
              {t("folders.tasks.write_question")}
              <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
            </Link>
          ) : (
            reviewLinks.map(([to, label], index) => (
              <Link key={to} to={to} state={linkState} className={index ? quietLinkClass : linkClass}>
                {label}
                {!index && <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />}
              </Link>
            ))
          )}
        </div>
      )}
    </article>
  );
}
